import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Award, ExternalLink, ArrowLeft, MapPin, CheckCircle2, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import { verifyBadge } from "@/lib/api";
import { SkillTag } from "@/components/shared";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function BadgeVerify() {
  const { hash } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    verifyBadge(hash).then(setData).catch(() => setNotFound(true));
  }, [hash]);

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Verify link copied");
  };

  if (notFound) {
    return (
      <div className="min-h-screen grain flex flex-col items-center justify-center px-6 text-center">
        <XCircle className="w-14 h-14 text-destructive" />
        <h1 className="mt-4 font-heading text-2xl font-bold">Credential not found</h1>
        <p className="text-muted-foreground mt-1">The verification code is invalid or the credential was revoked.</p>
        <Button className="mt-6" onClick={() => navigate("/")}>Go home</Button>
      </div>
    );
  }
  if (!data) {
    return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-4">
      <Skeleton className="h-48 rounded-xl" />
      <Skeleton className="h-40 rounded-xl" />
    </div>;
  }

  const { badge, worker, skills } = data;
  const issued = new Date(badge.issued_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen grain">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="verify-back-btn" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-border bg-card p-6 sm:p-8" data-testid="verify-card">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-bold">
              <ShieldCheck className="w-3.5 h-3.5" /> SkillBridge Verified
            </div>
            <span data-testid="verify-authentic-pill" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200 px-3 py-1 text-xs font-bold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Authentic
            </span>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center gap-6">
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 rounded-lg rotate-45 bg-primary shadow-lg shadow-primary/25" />
              <div className="absolute inset-2 rounded-lg rotate-45 border-2 border-white/40" />
              <div className="absolute inset-0 flex items-center justify-center"><Award className="w-10 h-10 text-white" /></div>
            </div>
            <div className="text-center sm:text-left">
              <h1 data-testid="verify-badge-title" className="font-heading text-2xl sm:text-3xl font-black tracking-tight">{badge.badge_title}</h1>
              <p className="mt-1 text-sm text-muted-foreground">Issued to <Link data-testid="verify-worker-link" to={`/resume/${worker.id}`} className="font-semibold text-foreground hover:text-primary">{worker.name}</Link></p>
              <p className="mt-0.5 text-xs text-muted-foreground">Verify code <span className="font-mono">#{badge.public_verify_hash}</span></p>
              <p className="mt-0.5 text-xs text-muted-foreground">Issued {issued}</p>
            </div>
          </div>
        </motion.div>

        <section className="mt-6 rounded-xl border border-border bg-card p-6 sm:p-8">
          <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">Credentialed skills</h2>
          <div className="flex flex-wrap gap-2.5">
            {skills.length ? skills.map((s) => <SkillTag key={s.id} skill={s} testid={`verify-skill-${s.id}`} />)
              : <span className="text-sm text-muted-foreground">—</span>}
          </div>
        </section>

        <section className="mt-6 rounded-xl border border-border bg-card p-6 sm:p-8">
          <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">Issued to</h2>
          <button onClick={() => navigate(`/resume/${worker.id}`)} className="w-full text-left flex items-center gap-4">
            <Avatar className="w-14 h-14 border border-border">
              <AvatarImage src={worker.avatar_url} className="object-cover" />
              <AvatarFallback>{worker.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-bold text-lg">{worker.name}</div>
              <div className="text-sm text-muted-foreground truncate">{worker.headline}</div>
              {worker.location && <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{worker.location}</div>}
            </div>
            <ExternalLink className="w-4 h-4 text-primary" />
          </button>
        </section>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Button data-testid="verify-copy-btn" variant="outline" onClick={share}>
            <Copy className="w-4 h-4 mr-1.5" /> Copy verify link
          </Button>
          <Button data-testid="verify-resume-btn" onClick={() => navigate(`/resume/${worker.id}`)}>
            <ExternalLink className="w-4 h-4 mr-1.5" /> Full resume
          </Button>
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Credentials issued & verified by SkillBridge · anyone with this link can verify authenticity.
        </p>
      </div>
    </div>
  );
}
